import React, { useState, useEffect } from 'react';
import {
  Typography, Box, TextField, Button, MenuItem, Stack, Card, CardContent, Alert, Paper
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { useToast } from '../context/ToastContext';
import { useDispatch, useSelector } from 'react-redux';
import { fetchSingleStaff, updateStaff } from '../slices/staffSlice';
import { fetchEmployeeTypes } from '../slices/employeeTypesSlice';
import { motion } from 'framer-motion';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';

const MotionPaper = motion(Paper);
const MotionCard = motion(Card);

const statuses = ['نشط', 'غير نشط'];
const PHONE_CODE_OPTIONS = [
  { value: '972', label: '+972', flag: '🇵🇸' },
  { value: '970', label: '+970', flag: '🇵🇸' }
];

const EditStaffPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const dispatch = useDispatch();

  const employeeTypes = useSelector((state) => state.employeeTypes.items);
  const staff = useSelector((state) => state.staff.current);

  const [localStaff, setLocalStaff] = useState(null);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [newContractFile, setNewContractFile] = useState(null);
  const [hasExistingContract, setHasExistingContract] = useState(false);

  // Load staff data
  useEffect(() => {
    dispatch(fetchEmployeeTypes());
    dispatch(fetchSingleStaff(id))
      .unwrap()
      .then(data => {
        setLocalStaff(data);
        setHasExistingContract(!!data.contract_pdf || !!data.contract_pdf_url);
      })
      .catch(() => {
        showToast('الموظف غير موجود', 'error');
        navigate('/staff');
      });
  }, [dispatch, id, navigate, showToast]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setLocalStaff(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const { name, files } = e.target;
    if (files && files[0]) {
      const file = files[0];
      
      // Validate file type
      if (name === 'contract_pdf' && !file.type.includes('pdf')) {
        showToast('يجب أن يكون الملف من نوع PDF', 'error');
        return;
      }
      
      // Validate file size (e.g., max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        showToast('حجم الملف كبير جداً. الحد الأقصى 10 ميجابايت', 'error');
        return;
      }

      setNewContractFile(file);
      setLocalStaff(prev => ({ ...prev, [name]: file }));
    }
  };

  const validate = () => {
    const tempErrors = {};
    if (!localStaff.first_name) tempErrors.first_name = 'مطلوب';
    if (!localStaff.last_name) tempErrors.last_name = 'مطلوب';
    if (!localStaff.employee_type) tempErrors.employee_type = 'مطلوب';
    if (!localStaff.phone_number) tempErrors.phone_number = 'مطلوب';
    if (!localStaff.start_date) tempErrors.start_date = 'مطلوب';
    if (!localStaff.base_salary) tempErrors.base_salary = 'مطلوب';
    setErrors(tempErrors);
    return Object.keys(tempErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    try {
      // Create a clean copy of the staff data for submission
      const staffToSubmit = {
        ...localStaff,
        // Only include the contract_pdf if a new file was uploaded
        ...(newContractFile && { contract_pdf: newContractFile })
      };

      await dispatch(updateStaff({ id, staff: staffToSubmit })).unwrap();
      showToast('تم تحديث بيانات الموظف بنجاح!', 'success');
      navigate('/staff');
    } catch (err) {
      console.error(err);
      showToast('فشل في تحديث البيانات', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => navigate('/staff');

  const removeNewContract = () => {
    setNewContractFile(null);
    setLocalStaff(prev => {
      const updated = { ...prev };
      delete updated.contract_pdf;
      return updated;
    });
  };

  if (!localStaff) return null;

  // 🎨 Design System tokens
  const fontFamily = 'Cairo, Tajawal, sans-serif';
  const cardSx = {
    borderRadius: 3,
    bgcolor: '#fff',
    boxShadow: '0 2px 12px rgba(67,56,202,0.07)',
    border: '1px solid #E5E7EB',
  };
  const fieldSx = {
    fontFamily,
    '& .MuiInputBase-root': { borderRadius: 2, fontFamily, bgcolor: '#fff' },
    '& .MuiInputLabel-root': { fontFamily, color: '#6B7280' },
    '& .MuiInputLabel-root.Mui-focused': { color: '#4338CA' },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E5E7EB' },
    '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#C7D2FE' },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#4338CA' },
    '& .MuiFormHelperText-root': { fontFamily },
    '& input, & .MuiSelect-select': { color: '#1E1B4B' },
  };
  const SectionHeader = ({ icon, children }) => (
    <Stack useFlexGap direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
      <Box
        sx={{
          width: 34,
          height: 34,
          borderRadius: 2,
          bgcolor: '#EEF2FF',
          color: '#4338CA',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {icon}
      </Box>
      <Typography sx={{ fontWeight: 700, color: '#1E1B4B', fontFamily, fontSize: '1.05rem' }}>
        {children}
      </Typography>
    </Stack>
  );

  return (
    // 🎨 Design System Applied
    <Box
      component={motion.div}
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: 'easeOut' }}
      sx={{
        direction: 'rtl',
        fontFamily,
        p: { xs: 2, md: 3 },
        bgcolor: '#F8F9FF',
        minHeight: '100vh',
      }}
    >
      <Box sx={{ maxWidth: 900, mx: 'auto' }}>
        {/* Header Section */}
        <MotionPaper
          component={motion.div}
          elevation={0}
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          sx={{
            p: { xs: 2, sm: 3 },
            mb: 3,
            borderRadius: 3,
            background: 'linear-gradient(135deg, #00BFFF 0%, #4338CA 60%, #5B21F4 100%)',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Stack useFlexGap direction="row" alignItems="center" spacing={1.5}>
            <Box
              sx={{
                bgcolor: 'rgba(255,255,255,0.15)',
                borderRadius: 2.5,
                width: 52,
                height: 52,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <BadgeOutlinedIcon sx={{ color: '#fff', fontSize: 30 }} />
            </Box>
            <Box>
              <Typography variant="h5" fontWeight={800} color="#fff" sx={{ fontFamily }}>
                تعديل بيانات الموظف
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: 'rgba(255,255,255,0.8)', mt: 0.25, fontFamily }}
              >
                تحديث المعلومات العامة والرواتب والعقد للموظف
              </Typography>
            </Box>
          </Stack>

          <Button
            variant="outlined"
            startIcon={<ArrowForwardIcon />}
            onClick={handleCancel}
            disabled={loading}
            sx={{
              color: '#fff',
              borderColor: 'rgba(255,255,255,0.6)',
              px: 3,
              py: 1.1,
              borderRadius: 2.5,
              fontWeight: 700,
              textTransform: 'none',
              fontFamily,
              '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.12)' },
            }}
          >
            العودة للموظفين
          </Button>
        </MotionPaper>

        <form onSubmit={handleSubmit}>
          <Stack spacing={3}>
            {/* معلومات عامة */}
            <MotionCard
              elevation={0}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35, delay: 0.1, ease: 'easeOut' }}
              sx={cardSx}
            >
              <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <SectionHeader icon={<PersonOutlineIcon fontSize="small" />}>معلومات عامة</SectionHeader>
                <Stack spacing={2}>
                  <TextField
                    label="الاسم الأول"
                    name="first_name"
                    value={localStaff.first_name || ''}
                    onChange={handleChange}
                    error={!!errors.first_name}
                    helperText={errors.first_name}
                    dir="rtl"
                    sx={fieldSx}
                  />
                  <TextField
                    label="اسم العائلة"
                    name="last_name"
                    value={localStaff.last_name || ''}
                    onChange={handleChange}
                    error={!!errors.last_name}
                    helperText={errors.last_name}
                    dir="rtl"
                    sx={fieldSx}
                  />
                  <TextField
                    select
                    label="القسم"
                    name="employee_type"
                    value={localStaff.employee_type || ''}
                    onChange={handleChange}
                    error={!!errors.employee_type}
                    helperText={errors.employee_type}
                    dir="rtl"
                    sx={fieldSx}
                  >
                    {employeeTypes.map(type => (
                      <MenuItem key={type.id} value={type.id}>{type.display_value}</MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    label="البريد الإلكتروني"
                    name="email"
                    value={localStaff.email || ''}
                    onChange={handleChange}
                    dir="rtl"
                    sx={fieldSx}
                  />
                  <Stack useFlexGap direction="row" spacing={1}>

                  <TextField
                    label="رقم الهاتف"
                    name="phone_number"
                    value={localStaff.phone_number || ''}
                    onChange={handleChange}
                    error={!!errors.phone_number}
                    helperText={errors.phone_number}
                    dir="rtl"
                    fullWidth
                    sx={fieldSx}
                  />
                  <TextField
                    select
                    label="رمز الدولة"
                    name="phone_code"
                    value={localStaff.phone_code || '972'}
                    onChange={handleChange}
                    dir="rtl"
                    sx={{ ...fieldSx, minWidth: 120 }}
                  >
                    {PHONE_CODE_OPTIONS.map(option => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.flag} {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Stack>
                  <TextField
                    label="تاريخ التعيين"
                    name="start_date"
                    type="date"
                    value={localStaff.start_date || ''}
                    onChange={handleChange}
                    InputLabelProps={{ shrink: true }}
                    error={!!errors.start_date}
                    helperText={errors.start_date}
                    dir="rtl"
                    sx={fieldSx}
                  />
                  <TextField
                    select
                    label="الحالة"
                    name="is_archived"
                    value={localStaff.is_archived ? 'غير نشط' : 'نشط'}
                    onChange={(e) => setLocalStaff(prev => ({ ...prev, is_archived: e.target.value === 'غير نشط' }))}
                    dir="rtl"
                    sx={fieldSx}
                  >
                    {statuses.map(status => (
                      <MenuItem key={status} value={status}>{status}</MenuItem>
                    ))}
                  </TextField>
                </Stack>
              </CardContent>
            </MotionCard>

            {/* الرواتب */}
            <MotionCard
              elevation={0}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35, delay: 0.15, ease: 'easeOut' }}
              sx={cardSx}
            >
              <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <SectionHeader icon={<PaymentsOutlinedIcon fontSize="small" />}>معلومات الرواتب والساعات</SectionHeader>
                <Stack spacing={2}>
                  <TextField
                    label="الراتب الأساسي"
                    name="base_salary"
                    type="number"
                    value={localStaff.base_salary || ''}
                    onChange={handleChange}
                    error={!!errors.base_salary}
                    helperText={errors.base_salary}
                    dir="rtl"
                    sx={fieldSx}
                  />
                </Stack>
              </CardContent>
            </MotionCard>

            {/* العقد */}
            <MotionCard
              elevation={0}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35, delay: 0.2, ease: 'easeOut' }}
              sx={cardSx}
            >
              <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <SectionHeader icon={<DescriptionOutlinedIcon fontSize="small" />}>معلومات العقد</SectionHeader>
                <Stack spacing={2}>
                  {/* Current Contract */}
                  {hasExistingContract && (
                    <Alert
                      severity="info"
                      sx={{
                        textAlign: 'right',
                        borderRadius: 2,
                        fontFamily,
                        bgcolor: '#E0F2FE',
                        color: '#0284C7',
                        '& .MuiAlert-icon': { color: '#0284C7' },
                      }}
                    >
                      يوجد عقد محفوظ حالياً
                      {(localStaff.contract_pdf || localStaff.contract_pdf_url) && (
                        <Button
                          href={localStaff.contract_pdf || localStaff.contract_pdf_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{ ml: 1, fontWeight: 700, textTransform: 'none', fontFamily, color: '#0284C7' }}
                          size="small"
                        >
                          عرض العقد الحالي
                        </Button>
                      )}
                    </Alert>
                  )}

                  {/* New Contract Upload */}
                  {newContractFile && (
                    <Alert
                      severity="success"
                      sx={{
                        textAlign: 'right',
                        borderRadius: 2,
                        fontFamily,
                        bgcolor: '#DCFCE7',
                        color: '#16A34A',
                        '& .MuiAlert-icon': { color: '#16A34A' },
                      }}
                    >
                      تم اختيار ملف جديد: {newContractFile.name}
                      <Button
                        onClick={removeNewContract}
                        size="small"
                        color="error"
                        sx={{ ml: 1, fontWeight: 700, textTransform: 'none', fontFamily }}
                      >
                        إزالة
                      </Button>
                    </Alert>
                  )}

                  <Button
                    variant="outlined"
                    component="label"
                    fullWidth
                    startIcon={<UploadFileOutlinedIcon />}
                    sx={{
                      borderRadius: 2.5,
                      py: 1.25,
                      fontWeight: 700,
                      textTransform: 'none',
                      fontFamily,
                      borderColor: '#4338CA',
                      color: '#4338CA',
                      borderStyle: 'dashed',
                      '&:hover': { borderColor: '#3730A3', bgcolor: '#EEF2FF', borderStyle: 'dashed' },
                    }}
                  >
                    {newContractFile ? 'تغيير العقد' : (hasExistingContract ? 'رفع عقد جديد' : 'رفع عقد')}
                    <input
                      type="file"
                      hidden
                      name="contract_pdf"
                      accept=".pdf"
                      onChange={handleFileChange}
                    />
                  </Button>
                </Stack>
              </CardContent>
            </MotionCard>

            {/* الأزرار */}
            <Stack useFlexGap direction="row" spacing={2} justifyContent="center" sx={{ pt: 1 }}>
              <Button
                type="submit"
                variant="contained"
                disabled={loading}
                sx={{
                  bgcolor: '#4338CA',
                  color: '#fff',
                  px: 4,
                  py: 1.1,
                  borderRadius: 2.5,
                  boxShadow: 'none',
                  fontWeight: 700,
                  textTransform: 'none',
                  fontFamily,
                  '&:hover': { bgcolor: '#3730A3', boxShadow: 'none' },
                  '&.Mui-disabled': { bgcolor: '#C7D2FE', color: '#fff' },
                }}
              >
                {loading ? 'جاري الحفظ...' : 'حفظ التعديلات'}
              </Button>
              <Button
                variant="outlined"
                color="error"
                onClick={handleCancel}
                disabled={loading}
                sx={{
                  px: 4,
                  py: 1.1,
                  borderRadius: 2.5,
                  fontWeight: 700,
                  textTransform: 'none',
                  fontFamily,
                  borderColor: '#DC2626',
                  color: '#DC2626',
                  '&:hover': { borderColor: '#DC2626', bgcolor: '#FEE2E2' },
                }}
              >
                إلغاء
              </Button>
            </Stack>
          </Stack>
        </form>
      </Box>
    </Box>
  );
};

export default EditStaffPage;